import { useAtomValue } from "jotai";
import { Suspense } from "react";
import { Icon } from "@zapier/design-system";
import { Tooltip, Button } from "@zapier/design-system-beta";
import {
  DefaultNodeData,
  interfaceProjectAtomFamily_SUSPENDS,
  isTemplateInterfacePageId,
  isTemplateInterfaceProjectId,
} from "@zapier/canvas-state";

import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";
import { editInterfaceUrl } from "../../utils/editInterfaceUrl";

type Props = {
  nodeId: string;
  interfaceProjectId: DefaultNodeData["projectId"];
  interfacePageId: DefaultNodeData["pageId"];
};

function SuspendedInterfaceLink({
  nodeId,
  interfaceProjectId,
  interfacePageId,
}: Props) {
  const { emitEventOnAssetOpen } = useEmitUserInteractionEvents();

  const interfaceProject = useAtomValue(
    interfaceProjectAtomFamily_SUSPENDS({
      nodeId,
      interfaceProjectId,
    }),
  );

  const isTemplateId =
    (interfaceProjectId && isTemplateInterfaceProjectId(interfaceProjectId)) ||
    (interfacePageId && isTemplateInterfacePageId(interfacePageId));

  if (!interfaceProjectId || !interfaceProject || isTemplateId) {
    return null;
  }

  const interfaceLink = editInterfaceUrl(interfaceProjectId, interfacePageId);

  return (
    <Tooltip
      placement="top"
      content={`Edit ${interfacePageId ? "interface page" : "interface"}`}
    >
      <Tooltip.FocusableTrigger>
        <Button
          size="small"
          onPress={async () => {
            emitEventOnAssetOpen({
              asset: interfacePageId
                ? "Published Interface Page"
                : "Published Interface Project",
              location: "node",
            });
            window.open(interfaceLink, "_blank");
          }}
        >
          <Icon name="arrowOffsite" size={20} color="BackgroundDefault" />
          Edit
        </Button>
      </Tooltip.FocusableTrigger>
    </Tooltip>
  );
}

export function InterfaceLink({
  nodeId,
  interfaceProjectId,
  interfacePageId,
}: Props) {
  return (
    <Suspense fallback={null}>
      <SuspendedInterfaceLink
        nodeId={nodeId}
        interfaceProjectId={interfaceProjectId}
        interfacePageId={interfacePageId}
      />
    </Suspense>
  );
}
